import { AntDesign, Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useEffect, useState } from 'react';
import { StyleSheet, TouchableOpacity, TouchableOpacityProps } from 'react-native';

import { useDeleteFavorite, useSetFavorite } from '@/api/hooks/favoritesHooks';
import { FavoriteType } from '@/api/models/favoritesModels';
import { heartEmpty, heartFill } from '@/assets/images/icons';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useMeFavorites } from '@/contexts/favoritesContext';
import { i18n } from '@/utils/i18n';

interface FavoriteButtonProps extends TouchableOpacityProps {
  itemId: string;
  type: FavoriteType;
  size?: number;
  variant?: 'default' | 'outlined';
}

const FavoriteButton = (props: FavoriteButtonProps) => {
  const size = props.size ?? 30;
  const variant = props.variant ?? 'default';

  const { state } = useAuth();

  const { state: favoritesState, fetchFavorites, isFavsPending } = useMeFavorites();
  const { showError } = useEndFlowModal();

  const favItem = favoritesState.favorites?.find((item) => item.itemId === props.itemId);

  const [favorite, setFavorite] = useState<boolean>(false);

  const { mutateAsync: addToFavorite, isPending: isAddPending } = useSetFavorite();
  const { mutateAsync: deleteFavorite, isPending: isDeletePending } = useDeleteFavorite();

  //Locales
  const failure = i18n.t('end_flow.error_title');
  const failureMessage = i18n.t('favorites.failure_message');

  const disabled = isAddPending || isDeletePending || isFavsPending || !state.user?.user;

  const handleFavorite = async () => {
    try {
      if (favorite)
        await deleteFavorite({
          idFavorite: favItem?.favId ?? ''
        });
      else
        await addToFavorite({
          idFavorite: props.itemId,
          type: props.type
        });
      setFavorite(!favorite);
      fetchFavorites();
    } catch (error) {
      console.error(error);
      showError({
        title: failure,
        message: failureMessage
      });
    }
  };

  useEffect(() => {
    setFavorite(favItem !== undefined);
  }, [favItem]);

  return (
    <TouchableOpacity
      style={props.style}
      onPress={() => {
        !disabled && handleFavorite();
      }}>
      {getIcon(favorite, size, variant)}
    </TouchableOpacity>
  );
};

const getIcon = (favorite: boolean, size: number, variant: string) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { style } = useStyle(size);

  switch (variant) {
    case 'outlined':
      return favorite ? <AntDesign name="heart" size={size} /> : <Ionicons name="heart-outline" size={size} />;
    default:
      return favorite ? (
        <Image source={heartFill} style={style.favoriteIcon} contentFit="contain" />
      ) : (
        <Image source={heartEmpty} style={style.favoriteIcon} contentFit="contain" />
      );
  }
};

export default FavoriteButton;

const useStyle = (size: number) => {
  const style = StyleSheet.create({
    favoriteIcon: {
      height: size,
      width: size
    }
  });

  return { style };
};
